/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
	Button,
	IconName,
	MaybeElement,
} from '@blueprintjs/core';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { filterProperties } from 'utilities/srsUtils';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { InfiniteScrollingList } from '/views/list/infiniteScrollingList';
import { ColorAliases } from 'styles/variables';
import { FlexSplitter, HeaderMd, hoverRevealChildrenVisibility, interactiveListItem, selectedClassName } from 'styles/styledComponentUtils';
import { SetOfTotal } from 'components/setOfTotal';
import { Sizes } from 'styles/variables-slykit';
import { SortSelect } from 'components/sortSelect';
import { GenericObject } from 'interfaces/GenericObject';
import { sendUsageAction } from 'utilities/usageStats';

type ActionButton = { label?: string, icon?: IconName | MaybeElement, action?: (event: React.MouseEvent<HTMLElement>) => void }

interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	propertyList?: string[],
	sortOptions?: string[],
	sortPropertyName?: string,
	sortDescending?: boolean,
	heading?: string,
	rowRenderer: React.FunctionComponent<{
		item: GenericObject<any>,
		onSelect: () => void,
		sortPropertyName?: string,
	}>,
	rowPlaceholderRenderer?: React.FunctionComponent
	select?: boolean,
	actions?: ActionButton[],
	listRenderer?: React.FunctionComponent,
}

export const DocumentsList: React.FunctionComponent<Props> = ({
	propertyList, // = [], // ??
	sortOptions,
	sortPropertyName,
	sortDescending = false,
	heading = 'Documents',
	rowRenderer: Row,
	rowPlaceholderRenderer: RowPlaceholder,
	select: isRowSelectable = false,
	actions,
	listRenderer = undefined,
	...props
}) => {
	const {
		state: {
			explorer: {
				views: { documentViewer },
				selectedDefinitionName,
			},
			transient: { activeResult, rootResult },
		},
		dispatch,
	} = useContext(WorkspaceContext);
	const properties = useRef(propertyList);
	const [sortPropertyLocal, setSortPropertyLocal] = useState(sortPropertyName);
	const [sortDescendingLocal, setSortDescendingLocal] = useState(sortDescending);

	const onSelect = (item: GenericObject<any>) => {
		dispatch(
			WorkspaceActionCreator.updateViewProperties('documentViewer', {
				selectedDocumentId: item.documentId,
			})
		);
		sendUsageAction("DocumentOpened", item.externalId);
	};

	const WrappedRow = ({ item }) => {
		// TODO: this wrapper probably shouldn't exist
		// probably should be InfiniteScrollingList.rowRenderer=Row ?
		return (
			<div
				className={item.documentId === documentViewer?.selectedDocumentId ? `selected ${selectedClassName}` : undefined}
				css={[rowItemStyle, isRowSelectable ? interactiveListItem : undefined, hoverRevealChildrenVisibility]}
				onClick={isRowSelectable ? () => onSelect(item) : undefined} // This line is controversial ?
				cy-test="documents-list-item"
			>
				<Row item={item} onSelect={() => onSelect(item)} sortPropertyName={sortPropertyLocal} />
			</div>
		);
	};

	const filteredSortOptions: string[] = useMemo(() => {
		let filteredProperties = filterProperties(selectedDefinitionName, [], []).map(p => p.name)
		if (sortOptions) // if specified sort options are provided, only show those
			filteredProperties = filteredProperties.filter(name => sortOptions.includes(name))
		return filteredProperties
	}, [sortOptions, selectedDefinitionName])

	const renderActions = useCallback(() => {
		if (!actions) return null;
		return (
			<div css={actionsRowStyle}>
				{actions.map(({ label, icon, action }, i) => (
					<Button
						key={i}
						minimal
						onClick={action}
						text={label}
						icon={icon}
						small
					/>
				))}
			</div>
		);
	}, [actions]);

	const count = useMemo(() => activeResult === rootResult
		? {
			total: rootResult?.count,
		} : {
			total: rootResult?.count,
			set: activeResult?.count || '...',
			divider: ` of `,
		}, [activeResult, rootResult])

	return (
		<div css={wrapperStyle} {...props}>
			<section css={headerRowStyle}>
				<HeaderMd css={headingStyle}>{heading}</HeaderMd>
				<SetOfTotal {...count} css={countStyle} />
				<FlexSplitter />
				<SortSelect
					sortOptions={filteredSortOptions}
					selectedSortOption={sortPropertyLocal}
					setSelectedSortOption={setSortPropertyLocal}
					isDescending={sortDescendingLocal}
					setIsDescending={setSortDescendingLocal}
					css={sortSelectStyle}
				/>
			</section>
			{renderActions()}
			{listRenderer ?
				listRenderer :
				(<InfiniteScrollingList
					key={activeResult?.resultId + sortPropertyLocal + sortDescendingLocal || 0}
					properties={properties.current}
					rowRenderer={WrappedRow}
					rowPlaceholderRenderer={RowPlaceholder}
					sortProperty={sortPropertyLocal}
					sortDescending={sortDescendingLocal}
				/>)
			}
		</div>
	);
};

const wrapperStyle = css`
	flex: 1;
	width: 100%;
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

const headerRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	border-bottom: 1px solid ${ColorAliases.PT_DIVIDER_BLACK};
	padding: ${Sizes.md} ${Sizes.lg};
`;

const headingStyle = css`
	margin-right: ${Sizes.sm};
`
const countStyle = css`
	margin-right: ${Sizes.md};
`
const sortSelectStyle = css`
	margin: ${Sizes.xxsm} 0 0 -7px;
`

const actionsRowStyle = css`
	padding: ${Sizes.xsm} ${Sizes.lg};
	border-bottom: 1px solid ${ColorAliases.PT_DIVIDER_BLACK};
`
const rowItemStyle = css`
	padding: ${Sizes.md} ${Sizes.lg};
	border-bottom: 1px solid ${ColorAliases.PT_DIVIDER_BLACK};
`

